import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Package, Building, ClipboardCheck, Users, TrendingUp, ArrowRight } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { adminApi } from '../../utils/api'

interface Stats {
  packages_count: number
  publishers_count: number
  pending_reviews: number
  subscriptions_count: number
}

export default function AdminDashboardPage() {
  const { token, user } = useAuth()
  const [stats, setStats] = useState<Stats | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadStats() {
      if (!token) return

      console.log('[AdminDashboard] Loading stats')
      try {
        const data = await adminApi.getStats(token)
        setStats(data)
      } catch (err) {
        console.error('[AdminDashboard] Failed to load stats:', err)
        // Set default stats on error
        setStats({
          packages_count: 0,
          publishers_count: 0,
          pending_reviews: 0,
          subscriptions_count: 0,
        })
      } finally {
        setLoading(false)
      }
    }

    loadStats()
  }, [token])

  const statCards = [
    {
      label: 'Total Packages',
      value: stats?.packages_count ?? 0,
      icon: Package,
      color: 'bg-blue-500',
      link: '/admin/reviews',
    },
    {
      label: 'Publishers',
      value: stats?.publishers_count ?? 0,
      icon: Building,
      color: 'bg-green-500',
      link: '/admin/publishers',
    },
    {
      label: 'Pending Reviews',
      value: stats?.pending_reviews ?? 0,
      icon: ClipboardCheck,
      color: 'bg-yellow-500',
      link: '/admin/reviews',
    },
    {
      label: 'Subscriptions',
      value: stats?.subscriptions_count ?? 0,
      icon: TrendingUp,
      color: 'bg-purple-500',
      link: '/admin/users',
    },
  ]

  return (
    <div className="animate-fade-in">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500 mt-1">
          Welcome back, {user?.name || user?.email}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat) => (
          <Link
            key={stat.label}
            to={stat.link}
            className="card p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{stat.label}</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {loading ? (
                    <span className="inline-block w-12 h-8 bg-gray-200 rounded animate-pulse" />
                  ) : (
                    stat.value.toLocaleString()
                  )}
                </p>
              </div>
              <div className={`${stat.color} p-3 rounded-lg`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="space-y-3">
            <Link
              to="/admin/reviews"
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <ClipboardCheck className="w-5 h-5 text-gray-400" />
                <span className="text-gray-700">Review pending packages</span>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400" />
            </Link>
            <Link
              to="/admin/publishers"
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <Building className="w-5 h-5 text-gray-400" />
                <span className="text-gray-700">Manage publishers</span>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400" />
            </Link>
            <Link
              to="/admin/users"
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <Users className="w-5 h-5 text-gray-400" />
                <span className="text-gray-700">View users</span>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400" />
            </Link>
          </div>
        </div>

        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">System Status</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">API Service</span>
              <span className="flex items-center text-green-600">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-2" />
                Healthy
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Sandbox Service</span>
              <span className="flex items-center text-green-600">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-2" />
                Healthy
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Worker Service</span>
              <span className="flex items-center text-green-600">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-2" />
                Healthy
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Database</span>
              <span className="flex items-center text-green-600">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-2" />
                Healthy
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
